
import axios from 'axios'
import { ErrorUtils } from '@/commons/utils/error'
import { ComboApplicationVue } from '@/combo-application/base'
import { PathType } from '@/combo-application/constants/router'
import { Component, Prop, Watch } from 'vue-property-decorator'
import ProgressBar from '@/commons/components/core/ProgressBar.vue'
import LoadingSpinner from '@/commons/components/core/LoadingSpinner.vue'
import { ExcludedApplicationReason } from '@/commons/constants/classifier'
import { ValidationError, PersonBiometryComponent } from '@/commons/types'
import PlaceOfIssue from '@/commons/components/application-create/PlaceOfIssue.vue'
import ApplicantInfo from '@/commons/components/application-create/ApplicantInfo.vue'
import PersonDetails from '@/commons/components/application-create/PersonDetails.vue'
import ApplicationReasons from '@/commons/components/application-create/ApplicationReasons.vue'
import PersonBiometry from '@/commons/components/application-create/biometry/PersonBiometry.vue'
import CertificationApproval from '@/commons/components/application-create/CertificationApproval.vue'
import PersonContacts from '@/commons/components/application-create/person-contacts/PersonContacts.vue'
import PreviousApplications from '@/commons/components/application-create/PreviousApplications.vue'

import {
    applicantStore,
    PaymentService,
    ApplicantService,
    classifiersStore,
    ApplicationService
} from '@/store'
import {
    validatorIds,
    CitizenPassportValidationFieldName,
    IdCardValidationFieldName
} from '@/commons/constants/application'
import { RouterUtils } from '@/commons/utils/router'

@Component({
    components: {
        ProgressBar,
        PlaceOfIssue,
        PersonDetails,
        PreviousApplications,
        ApplicantInfo,
        LoadingSpinner,
        PersonBiometry,
        PersonContacts,
        ApplicationReasons,
        CertificationApproval
    }
})
export default class ComboApplicationCreate extends ComboApplicationVue {
    @Prop() personCodeFromUrl!: string

    isSaving = false
    scrollToFirstError = false
    domValidatorIds = validatorIds
    idPlaceOfIssueTypeValidationId = IdCardValidationFieldName.DELIVERY_TYPE
    citizenPassportPlaceOfIssueTypeValidationId = IdCardValidationFieldName.DELIVERY_TYPE
    idPlaceOfIssueLocationValidationId = IdCardValidationFieldName.DELIVERY_LOCATION
    citizenPassportPlaceOfIssueLocationValidationId = CitizenPassportValidationFieldName.DELIVERY_LOCATION
    idApplicationReasonValidationId = IdCardValidationFieldName.REASON
    citizenPassportApplicationReasonValidationId = CitizenPassportValidationFieldName.REASON
    citizenPassportCourierCountryId = CitizenPassportValidationFieldName.COURIER_DELIVERY_COUNTRY
    citizenPassportCourierCityId = CitizenPassportValidationFieldName.COURIER_DELIVERY_CITY
    citizenPassportCourierPostalCodeId = CitizenPassportValidationFieldName.COURIER_DELIVERY_POSTAL_CODE

    idCardValidationAuthorizedPersonFirstNameFieldName = IdCardValidationFieldName.AUTHORIZED_FIRST_NAME
    idCardValidationAuthorizedPersonLastNameFieldName = IdCardValidationFieldName.AUTHORIZED_LAST_NAME
    idCardValidationAuthorizedPersonCodeFieldName = IdCardValidationFieldName.AUTHORIZED_PERSON_CODE

    citizenPassportValidationAuthorizedPersonFirstNameFieldName = CitizenPassportValidationFieldName.AUTHORIZED_FIRST_NAME
    citizenPassportValidationAuthorizedPersonLastNameFieldName = CitizenPassportValidationFieldName.AUTHORIZED_LAST_NAME
    citizenPassportValidationAuthorizedPersonCodeFieldName = CitizenPassportValidationFieldName.AUTHORIZED_PERSON_CODE

    excludedIdCardApplicationReasons: Array<string> = [
        ExcludedApplicationReason.ADDITIONAL_PASSPORT,
        ExcludedApplicationReason.PAGES_FULL
    ]

    created (): void {
        document.title = this.$tc('metadata.applicationData')
        ApplicantService.fetchApplicantInfo(this.personCodeFromUrl, this.applicationType)
    }

    @Watch('errors')
    observeErrors (newVal: Record<string, Array<ValidationError>>) {
        if (this.scrollToFirstError && ErrorUtils.hasValidationErrors(this, newVal)) {
            ErrorUtils.scrollToFirstError(this, newVal)
            this.scrollToFirstError = false
        }
    }

    get isLoading (): boolean {
        return !applicantStore.isValidApplicant || !classifiersStore.isValidClassifiers
    }

    get isPhotoPreliminaryCheckRunning (): boolean {
        return applicantStore.isPhotoPreliminaryCheckRunning
    }

    async validateForm (isSilent = false): Promise<boolean> {
        return this.validate({ silent: isSilent })
    }

    async saveApplication (): Promise<void> {
        (this.$refs.biometry as PersonBiometryComponent).handleSaving()
        await this.$nextTick()

        if (!await this.validateForm()) {
            this.scrollToFirstError = true
            return
        }

        try {
            this.isSaving = true
            await ApplicationService.saveApplication(this.applicationType)
            await PaymentService.fetchStateFee(applicantStore.getApplication.uuid)
            await this.$router.push({
                name: PathType.SUMMARY,
                params: { personCodeFromUrl: this.personCodeFromUrl }
            }, RouterUtils.setFocusToBody)
        } catch (e) {
            if (axios.isCancel(e)) {
                return
            }
            ErrorUtils.handleSavingError(e, this)
            this.isSaving = false
            this.scrollToFirstError = true
        }
    }
}
